import { Theme } from './types';
import { defaultTheme } from './themes/default';

export class ThemeManager {
  private static instance: ThemeManager;
  private currentTheme: Theme;
  private themes: Map<string, Theme>;
  private root: HTMLElement;
  
  private constructor() {
    this.themes = new Map();
    this.root = document.documentElement;
    this.currentTheme = defaultTheme;
    
    // Register default theme
    this.registerTheme(defaultTheme);
    
    // Load saved theme preference
    this.loadSavedTheme();
  }
  
  public static getInstance(): ThemeManager {
    if (!ThemeManager.instance) {
      ThemeManager.instance = new ThemeManager();
    }
    return ThemeManager.instance;
  }
  
  /**
   * Register a new theme
   */
  public registerTheme(theme: Theme): void {
    this.themes.set(theme.id, theme);
  }
  
  /**
   * Register multiple themes at once
   */
  public registerThemes(themes: Theme[]): void {
    themes.forEach(theme => this.registerTheme(theme));
  }
  
  /**
   * Get all registered themes
   */
  public getThemes(): Theme[] {
    return Array.from(this.themes.values());
  }
  
  /**
   * Get current active theme
   */
  public getCurrentTheme(): Theme {
    return this.currentTheme;
  }
  
  /**
   * Get theme by ID
   */
  public getTheme(themeId: string): Theme | undefined {
    return this.themes.get(themeId);
  }
  
  /**
   * Apply a theme by ID
   */
  public applyTheme(themeId: string): boolean {
    const theme = this.themes.get(themeId);
    if (!theme) {
      console.error(`Theme with ID "${themeId}" not found`);
      return false;
    }
    
    this.currentTheme = theme;
    this.applyThemeToDOM(theme);
    this.saveThemePreference(themeId);
    
    // Dispatch theme change event
    window.dispatchEvent(new CustomEvent('themechange', { detail: theme }));
    
    return true;
  }
  
  /**
   * Apply theme properties to DOM as CSS variables
   */
  private applyThemeToDOM(theme: Theme): void {
    // Apply color variables
    Object.entries(theme.colors).forEach(([key, value]) => {
      this.root.style.setProperty(`--color-${this.camelToKebab(key)}`, value);
    });
    
    // Apply typography variables
    Object.entries(theme.typography).forEach(([key, value]) => {
      this.root.style.setProperty(`--typography-${this.camelToKebab(key)}`, String(value));
    });
    
    // Apply spacing variables
    Object.entries(theme.spacing).forEach(([key, value]) => {
      this.root.style.setProperty(`--spacing-${key}`, value);
    });
    
    // Apply border radius variables
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
      this.root.style.setProperty(`--radius-${key}`, value);
    });
    
    // Apply shadow variables
    Object.entries(theme.shadows).forEach(([key, value]) => {
      this.root.style.setProperty(`--shadow-${key}`, value);
    });
    
    // Apply transition variables
    Object.entries(theme.transitions).forEach(([key, value]) => {
      this.root.style.setProperty(`--transition-${key}`, value);
    });
    
    // Apply component-specific variables
    this.applyComponentStyles(theme.components);
    
    // Apply z-index variables
    Object.entries(theme.zIndex).forEach(([key, value]) => {
      this.root.style.setProperty(`--z-${this.camelToKebab(key)}`, String(value));
    });
    
    // Apply breakpoint variables
    Object.entries(theme.breakpoints).forEach(([key, value]) => {
      this.root.style.setProperty(`--breakpoint-${key}`, value);
    });
    
    // Set theme attribute for CSS selectors
    this.root.setAttribute('data-theme', theme.id);
  }
  
  /**
   * Apply component-specific styles
   */
  private applyComponentStyles(components: Theme['components']): void {
    // Button styles
    const button = components.button;
    this.root.style.setProperty('--btn-padding-x', button.paddingX);
    this.root.style.setProperty('--btn-padding-y', button.paddingY);
    this.root.style.setProperty('--btn-font-size', button.fontSize);
    this.root.style.setProperty('--btn-font-weight', String(button.fontWeight));
    this.root.style.setProperty('--btn-border-radius', button.borderRadius);
    this.root.style.setProperty('--btn-transition', button.transition);
    
    // Button variants
    Object.entries(button).forEach(([variant, styles]) => {
      if (typeof styles === 'object') {
        Object.entries(styles).forEach(([prop, value]) => {
          this.root.style.setProperty(`--btn-${variant}-${this.camelToKebab(prop)}`, value);
        });
      }
    });
    
    // Input styles
    const input = components.input;
    Object.entries(input).forEach(([key, value]) => {
      this.root.style.setProperty(`--input-${this.camelToKebab(key)}`, String(value));
    });
    
    // Card styles
    const card = components.card;
    Object.entries(card).forEach(([key, value]) => {
      this.root.style.setProperty(`--card-${this.camelToKebab(key)}`, value);
    });
    
    // Modal styles
    const modal = components.modal;
    Object.entries(modal).forEach(([key, value]) => {
      this.root.style.setProperty(`--modal-${this.camelToKebab(key)}`, value);
    });
    
    // Table styles
    const table = components.table;
    Object.entries(table).forEach(([key, value]) => {
      this.root.style.setProperty(`--table-${this.camelToKebab(key)}`, String(value));
    });
    
    // Navigation styles
    const navigation = components.navigation;
    Object.entries(navigation).forEach(([key, value]) => {
      this.root.style.setProperty(`--nav-${this.camelToKebab(key)}`, value);
    });
    
    // Badge styles
    const badge = components.badge;
    Object.entries(badge).forEach(([key, value]) => {
      if (typeof value === 'string' || typeof value === 'number') {
        this.root.style.setProperty(`--badge-${this.camelToKebab(key)}`, String(value));
      }
    });
    
    // Tooltip styles
    const tooltip = components.tooltip;
    Object.entries(tooltip).forEach(([key, value]) => {
      this.root.style.setProperty(`--tooltip-${this.camelToKebab(key)}`, value);
    });
    
    // Dropdown styles
    const dropdown = components.dropdown;
    Object.entries(dropdown).forEach(([key, value]) => {
      this.root.style.setProperty(`--dropdown-${this.camelToKebab(key)}`, value);
    });
    
    // Progress styles
    const progress = components.progress;
    Object.entries(progress).forEach(([key, value]) => {
      this.root.style.setProperty(`--progress-${this.camelToKebab(key)}`, value);
    });
    
    // Alert styles
    const alert = components.alert;
    this.root.style.setProperty('--alert-padding', alert.padding);
    this.root.style.setProperty('--alert-border-radius', alert.borderRadius);
    this.root.style.setProperty('--alert-font-size', alert.fontSize);
    
    // Alert variants
    Object.entries(alert).forEach(([variant, styles]) => {
      if (typeof styles === 'object') {
        Object.entries(styles).forEach(([prop, value]) => {
          this.root.style.setProperty(`--alert-${variant}-${this.camelToKebab(prop)}`, value);
        });
      }
    });
    
    // Sidebar styles
    const sidebar = components.sidebar;
    Object.entries(sidebar).forEach(([key, value]) => {
      this.root.style.setProperty(`--sidebar-${this.camelToKebab(key)}`, value);
    });
    
    // Notes editor styles
    const notes = components.notes;
    Object.entries(notes).forEach(([key, value]) => {
      this.root.style.setProperty(`--notes-${this.camelToKebab(key)}`, String(value));
    });
  }
  
  /**
   * Convert camelCase to kebab-case
   */
  private camelToKebab(str: string): string {
    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }
  
  /**
   * Save theme preference to localStorage
   */
  private saveThemePreference(themeId: string): void {
    localStorage.setItem('focusHubTheme', themeId);
  }
  
  /**
   * Load saved theme preference from localStorage
   */
  private loadSavedTheme(): void {
    const savedThemeId = localStorage.getItem('focusHubTheme');
    if (savedThemeId && this.themes.has(savedThemeId)) {
      this.applyTheme(savedThemeId);
    } else {
      // Apply default theme
      this.applyThemeToDOM(this.currentTheme);
    }
  }
  
  /**
   * Create a custom theme based on an existing theme
   */
  public createCustomTheme(baseThemeId: string, overrides: Partial<Theme>): Theme | null {
    const baseTheme = this.themes.get(baseThemeId);
    if (!baseTheme) {
      console.error(`Base theme with ID "${baseThemeId}" not found`);
      return null;
    }
    
    // Deep merge the base theme with overrides
    const customTheme: Theme = this.deepMerge(baseTheme, overrides) as Theme;
    
    // Ensure the custom theme has a unique ID
    if (!customTheme.id || customTheme.id === baseTheme.id) {
      customTheme.id = `custom-${Date.now()}`;
    }
    
    return customTheme;
  }
  
  /**
   * Deep merge two objects
   */
  private deepMerge(target: any, source: any): any {
    const output = { ...target };
    
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            output[key] = source[key];
          } else {
            output[key] = this.deepMerge(target[key], source[key]);
          }
        } else {
          output[key] = source[key];
        }
      });
    }
    
    return output;
  }
  
  /**
   * Check if value is an object
   */
  private isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }
  
  /**
   * Export current theme as JSON
   */
  public exportTheme(): string {
    return JSON.stringify(this.currentTheme, null, 2);
  }
  
  /**
   * Import theme from JSON
   */
  public importTheme(themeJson: string): boolean {
    try {
      const theme = JSON.parse(themeJson) as Theme;
      
      // Validate theme structure (basic validation)
      if (!theme.id || !theme.name || !theme.colors || !theme.typography) {
        throw new Error('Invalid theme structure');
      }
      
      this.registerTheme(theme);
      return true;
    } catch (error) {
      console.error('Failed to import theme:', error);
      return false;
    }
  }
}